import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServiceBase } from 'app/core/services/service.base';
import { AppConstants } from 'app/app.constants';
import {
  INTERCEPTInternalServerError,
  INTERCEPTUnauthorized,
} from 'app/core/interceptors/contexts';
import { environment } from 'environments/environment';
import { currentPageLocation } from 'app/utils/analytics-location.utils';
import { isInternalUser } from 'app/utils/internal-user.utils';

declare var gtag: Function;

const ANALYTICS_IDS: Record<string, { gaIds: string[]; gtmId: string }> = {
  PROD: { gaIds: ['G-GXVGFBDF15'], gtmId: 'GTM-TJK49GVV' },
  STAGE: { gaIds: ['G-GXVGFBDF15'], gtmId: 'GTM-TJK49GVV' },
  DEV: { gaIds: ['G-QG3XYSK1JD'], gtmId: '' },
  LOCAL: { gaIds: [], gtmId: '' },
};

export interface ConsentState {
  analytics: boolean;
  decided: boolean;
  timestamp: number;
  policyVersion: string;
  expiresAt?: number;
}

const POLICY_VERSION = '2.0';
const CONSENT_EXPIRY_MS = 180 * 24 * 60 * 60 * 1000; // 180 dias (LGPD: recoleta periódica)

const DEFAULT_STATE: ConsentState = {
  analytics: false,
  decided: false,
  timestamp: 0,
  policyVersion: POLICY_VERSION,
};

@Injectable()
export class ConsentService extends ServiceBase {
  private http = inject(HttpClient);
  private scriptsInjected = false;
  private _consent$ = new BehaviorSubject<ConsentState>(this.loadFromStorage());

  readonly consent$: Observable<ConsentState> = this._consent$.asObservable();

  readonly hasDecided$: Observable<boolean> = this._consent$.pipe(
    map((s) => s.decided && !this.isExpired(s)),
  );

  readonly hasAnalyticsConsent$: Observable<boolean> = this._consent$.pipe(
    map((s) => s.analytics && s.decided && !this.isExpired(s)),
  );

  constructor() {
    super();
  }

  hasAnalyticsConsent(): boolean {
    const s = this._consent$.getValue();
    return s.analytics && s.decided && !this.isExpired(s);
  }

  hasDecided(): boolean {
    const s = this._consent$.getValue();
    return s.decided && !this.isExpired(s);
  }

  acceptAll(): void {
    const now = Date.now();
    const state: ConsentState = {
      analytics: true,
      decided: true,
      timestamp: now,
      policyVersion: POLICY_VERSION,
      expiresAt: now + CONSENT_EXPIRY_MS,
    };
    this.persist(state);
    this.injectAnalyticsScripts();
    this.applyToGtag(true);
    this.syncWithBackend();
  }

  rejectAll(): void {
    const now = Date.now();
    const state: ConsentState = {
      analytics: false,
      decided: true,
      timestamp: now,
      policyVersion: POLICY_VERSION,
      expiresAt: now + CONSENT_EXPIRY_MS,
    };
    this.persist(state);
    this.applyToGtag(false);
    this.syncWithBackend();
  }

  revokeConsent(): void {
    // LGPD Art. 8º: revogação deve ser tão fácil quanto a outorga.
    // Reset to the undecided state — re-shows the banner on the next login.
    localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT);
    this._consent$.next({ ...DEFAULT_STATE });
    this.applyToGtag(false);
    this.syncWithBackend(true);
  }

  /**
   * Pulls the consent record from the backend (CosmosDB) and reconciles the
   * local cache. The backend is the cross-device source of truth, so a decision
   * made on another device (e.g. revoking elsewhere) is reflected here on login.
   * On a network/server error the local cache is kept untouched.
   */
  loadFromBackend(): void {
    const token = this.obterTokenUsuario();
    if (!token) return;

    const options = {
      ...this.obterAuthHeader(),
      // A consent fetch failure must never trigger global logout/maintenance.
      context: new HttpContext()
        .set(INTERCEPTInternalServerError, false)
        .set(INTERCEPTUnauthorized, false),
    };

    firstValueFrom(
      this.http.get<Partial<ConsentState>>(
        `${this.UrlServiceV1}consent`,
        options,
      ),
    )
      .then((doc) => {
        // No server-side record (204) or a stale policy version → undecided.
        if (
          !doc ||
          typeof doc.decided !== 'boolean' ||
          doc.policyVersion !== POLICY_VERSION
        ) {
          this.reconcileUndecided();
          return;
        }

        const timestamp = doc.timestamp ?? Date.now();
        const state: ConsentState = {
          analytics: !!doc.analytics,
          decided: doc.decided,
          timestamp,
          policyVersion: POLICY_VERSION,
          expiresAt: timestamp + CONSENT_EXPIRY_MS,
        };
        this.persist(state);

        if (state.analytics) {
          this.injectAnalyticsScripts();
          this.applyToGtag(true);
        } else {
          this.applyToGtag(false);
        }
      })
      .catch(() => {
        // Keep the local cache on failure.
      });
  }

  private reconcileUndecided(): void {
    localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT);
    this._consent$.next({ ...DEFAULT_STATE });
    this.applyToGtag(false);
  }

  /**
   * Persists the current consent state to the backend.
   * @param force when true, syncs even for the undecided state (used by
   *   reject/revoke so the rejection is recorded). The post-login reconcile
   *   call leaves it false so undecided users don't create noise records.
   */
  syncWithBackend(force = false): void {
    const token = this.obterTokenUsuario();
    if (!token) return;

    const state = this._consent$.getValue();
    if (!state.decided && !force) return;

    firstValueFrom(
      this.http.post(
        `${this.UrlServiceV1}consent`,
        state,
        this.obterAuthHeader(),
      ),
    ).catch(() => {});
  }

  private isExpired(state: ConsentState): boolean {
    if (!state.expiresAt) return false; // backward compat: dados sem expiresAt não expiram
    return Date.now() > state.expiresAt;
  }

  private loadFromStorage(): ConsentState {
    try {
      const raw = localStorage.getItem(
        AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT,
      );
      if (!raw) return { ...DEFAULT_STATE };

      const parsed = JSON.parse(raw) as ConsentState;

      // Consentimento expirado — força re-decisão
      if (this.isExpired(parsed)) {
        localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT);
        return { ...DEFAULT_STATE };
      }

      // Versão de política diferente — força re-decisão
      if (parsed.policyVersion !== POLICY_VERSION) {
        localStorage.removeItem(AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT);
        return { ...DEFAULT_STATE };
      }

      if (parsed.decided && parsed.analytics) {
        this.injectAnalyticsScripts();
        this.applyToGtag(true);
      }
      return parsed;
    } catch {
      return { ...DEFAULT_STATE };
    }
  }

  private persist(state: ConsentState): void {
    localStorage.setItem(
      AppConstants.KEYS_LOCAL_STORAGE.ISO_CONSENT,
      JSON.stringify(state),
    );
    this._consent$.next(state);
  }

  private applyToGtag(analytics: boolean): void {
    if (typeof gtag === 'undefined') return;
    gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  private injectAnalyticsScripts(): void {
    if (this.scriptsInjected || typeof document === 'undefined') return;
    this.scriptsInjected = true;

    const { gaIds, gtmId } =
      ANALYTICS_IDS[environment.environmentName] ?? ANALYTICS_IDS['DEV'];

    for (const gaId of gaIds) {
      if (typeof gtag !== 'undefined') {
        gtag('js', new Date());
        // The page view sent by `config` must not carry the full document
        // URL either (see `currentPageLocation`), and staff accounts send
        // none (see `isInternalUser`). This also runs from the `_consent$`
        // initializer; the session is already available there because
        // `ServiceBase` fields are set first.
        gtag('config', gaId, {
          page_location: currentPageLocation(),
          send_page_view: !isInternalUser(this.obterUsuarioLogado()?.id),
        });
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script);
    }

    if (gtmId) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        'gtm.start': Date.now(),
        event: 'gtm.js',
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.cssText = 'display:none;visibility:hidden';
      noscript.appendChild(iframe);
      document.body.prepend(noscript);
    }
  }
}
